/**
 * Module-level data cache + hook for Task page.
 *
 * All API data lives here at module scope, completely outside React's
 * render cycle. Components subscribe via useState + a notify mechanism.
 * This guarantees:
 *   - listTasks is called exactly once per uid
 *   - listExecutions is called exactly once per filter combo
 *   - Tab switches cause zero API requests (data is already cached)
 */

// Module-level store, not a component: fallback errors (an Error with an empty message)
// use the locale at the time of failure.
import i18n from '../../i18n';
import {
  scheduleTaskClient,
  type TaskDetail,
  type ExecutionRecord,
  type ExecutionStatus,
} from '../../client/ScheduleTaskClient';

// ── Public types ────────────────────────────────────────────────────────────

export interface ExecutionItemView {
  taskName: string;
  record: ExecutionRecord;
}

export interface TaskDataState {
  tasks: TaskDetail[];
  tasksLoading: boolean;
  tasksLoaded: boolean;
  tasksError: string | null;
  executions: ExecutionItemView[];
  execLoading: boolean;
  execError: string | null;
  execContentTypes: string[];
}

// ── Module-level state ──────────────────────────────────────────────────────

let state: TaskDataState = {
  tasks: [],
  tasksLoading: false,
  tasksLoaded: false,
  tasksError: null,
  executions: [],
  execLoading: false,
  execError: null,
  execContentTypes: [],
};

// Track what we've already fetched
let tasksFetchedForUid: string | null = null;
let execFetchedKey: string | null = null;
let tasksFetching = false;
let execRequestId = 0;

// Subscribers
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((fn) => fn());
}

function update(partial: Partial<TaskDataState>) {
  state = { ...state, ...partial };
  notify();
}

// ── Fetch tasks (once per uid) ──────────────────────────────────────────────

export function fetchTasks(uid: string, agent?: string) {
  const key = agent ? `${uid}|${agent}` : uid;
  if (tasksFetchedForUid === key || tasksFetching) return;
  tasksFetching = true;
  update({ tasksLoading: true, tasksError: null });

  scheduleTaskClient
    .listTasks({ owner: uid, ...(agent ? { agent } : {}) })
    .then((res) => {
      tasksFetchedForUid = key;
      update({ tasks: res.tasks ?? [], tasksLoading: false, tasksLoaded: true });
    })
    .catch((err: Error) => {
      update({ tasksError: err.message || i18n.t('tasks:scheduled.loadFailed'), tasksLoading: false });
    })
    .finally(() => {
      tasksFetching = false;
    });
}

// ── Fetch execution history (single request via ListExecutions) ──────────────
// Uses request-ID pattern: new filter changes always fire a request,
// stale responses from previous requests are discarded.

export function fetchExecutions(uid: string, days: number, statusFilter: string, agent?: string) {
  const key = `${uid}|${days}|${statusFilter}|${agent ?? ''}`;
  if (key === execFetchedKey) return;

  const thisRequest = ++execRequestId;
  update({ execLoading: true, execError: null });

  const startTime = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
  const statusParam = statusFilter === 'ALL' ? undefined : (statusFilter as ExecutionStatus);

  scheduleTaskClient
    .listExecutions({
      executor: uid,
      startTime,
      status: statusParam,
      ...(agent ? { agent } : {}),
    })
    .then((res) => {
      if (thisRequest !== execRequestId) return; // stale — discard

      const executions = (res.executions ?? []).map((rec) => {
        const task = state.tasks.find((t) => t.taskId === rec.taskId);
        return { taskName: task?.name ?? rec.taskId, record: rec };
      });

      const seen = new Set<string>();
      const types: string[] = [];
      executions.forEach((e) => {
        if (e.record.contentType && !seen.has(e.record.contentType)) {
          seen.add(e.record.contentType);
          types.push(e.record.contentType);
        }
      });

      execFetchedKey = key;
      update({ executions, execLoading: false, execContentTypes: types });
    })
    .catch((err: Error) => {
      if (thisRequest !== execRequestId) return; // stale — discard
      update({ execError: err.message || i18n.t('tasks:history.loadFailed'), execLoading: false });
    });
}

/** Force re-fetch tasks (ignores cache). */
export function refreshTasks(uid: string, agent?: string) {
  tasksFetchedForUid = null;
  tasksFetching = false;
  fetchTasks(uid, agent);
}

/** Call when filters change so next fetchExecutions will re-fetch. */
export function invalidateExecCache() {
  execFetchedKey = null;
}

/** Force re-fetch executions for the current filter combo (ignores cache). */
export function refreshExecutions(uid: string, days: number, statusFilter: string, agent?: string) {
  execFetchedKey = null;
  fetchExecutions(uid, days, statusFilter, agent);
}

// ── React hook ──────────────────────────────────────────────────────────────

import { useSyncExternalStore } from 'react';

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}

function getSnapshot(): TaskDataState {
  return state;
}

export function useTaskData(): TaskDataState {
  return useSyncExternalStore(subscribe, getSnapshot);
}
